import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { getProductsConfigurableBySkus } from '@pylot-data/api/operations/get-product'
import type { ProductInterface } from '@pylot-data/pylotschema'
import { isSearchEnrichmentCandidate } from './utils/isSearchConfigurableCandidate'

/**
 * Completa los tiles de Live Search con el producto de Magento, en un lote por
 * pagina de resultados.
 *
 * Nacio para el "agregar rapido" de los configurables (`configurable_options` y
 * `variants`, para que el hover del SRP responda igual que el de la PLP).
 *
 * EVO-263: tambien es el unico camino por el que `associated_products` llega al
 * SRP, porque `productSearch` del edge devuelve `ProductSearchProduct`, un tipo
 * de seis campos que no incluye nada de Magento. Por eso se enriquece TODO item
 * sin enriquecer y no solo los configurables: en Casaideas hay grupos de color
 * formados por productos simples (los que no tienen variacion de talla, p. ej.
 * "Cubrecama bordado King"), y filtrando por "C" en el SKU se quedaban sin la
 * fila de miniaturas.
 *
 * Que se enriquece y como se comporta el tile son decisiones distintas:
 * `isSearchEnrichmentCandidate` vs `isSearchConfigurableCandidate`.
 */
export function useEnrichSearchConfigurables(
    products: ProductInterface[],
    enabled: boolean
): ProductInterface[] {
    const router = useRouter()
    const locale = router.locale || 'es'
    const [enrichmentBySku, setEnrichmentBySku] = useState<
        Record<string, ProductInterface>
    >({})
    const requestedSkusRef = useRef<Set<string>>(new Set())

    const skusToEnrichKey = useMemo(() => {
        if (!enabled) return ''

        return products
            .filter(isSearchEnrichmentCandidate)
            .map((product) => product.sku)
            .filter((sku) => !enrichmentBySku[sku])
            .filter((sku) => !requestedSkusRef.current.has(sku))
            .sort()
            .join('|')
    }, [products, enabled, enrichmentBySku])

    useEffect(() => {
        if (!enabled || !skusToEnrichKey) return

        const skusToEnrich = skusToEnrichKey.split('|')
        skusToEnrich.forEach((sku) => requestedSkusRef.current.add(sku))
        let cancelled = false

        void (async () => {
            try {
                const bySku = await getProductsConfigurableBySkus(
                    skusToEnrich,
                    locale
                )
                if (cancelled) return
                setEnrichmentBySku((prev) => ({ ...prev, ...bySku }))
            } catch {
                // Tile-level hover fetch remains as fallback.
                skusToEnrich.forEach((sku) =>
                    requestedSkusRef.current.delete(sku)
                )
            }
        })()

        return () => {
            cancelled = true
        }
    }, [enabled, skusToEnrichKey, locale])

    return useMemo(() => {
        if (!enabled) return products

        return products.map((product) => {
            const sku = product?.sku
            if (!sku || !enrichmentBySku[sku]) return product
            return { ...product, ...enrichmentBySku[sku] }
        })
    }, [products, enrichmentBySku, enabled])
}
